import { inject, Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, switchMap, timer } from 'rxjs';
import { AuthTokenService } from '../auth/auth-token.service';

/** Routes opt in with `data: { preload: true }` (always) or `data: { preload: 'auth' }` (signed-in only). */
export type PreloadFlag = true | 'auth';

/** Long enough for the landing tab to settle and its own requests to go out first. */
export const TAB_PRELOAD_DELAY_MS: number = 2500;

/**
 * Preloads only the routes that are flagged for it — the bottom-tab roots — a moment after the app
 * has landed, so the first tap on each tab switches instantly instead of waiting for its chunk.
 * `PreloadAllModules` was dropped because it pulled every lazy route (13+ MB with Plotly) on first
 * visit; this keeps the budget to the handful of pages a tab tap can reach. Honours the Save-Data
 * hint, and `'auth'` routes (Home, which the auth guard bounces guests away from) only load when a
 * session exists.
 */
@Injectable({ providedIn: 'root' })
export class TabPreloadingStrategy implements PreloadingStrategy {
  private readonly authTokenService: AuthTokenService = inject(AuthTokenService);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const flag: PreloadFlag | undefined = route.data?.['preload'];
    if (!flag || this.isSaveDataOn()) {
      return of(null);
    }
    if (flag === 'auth' && !this.authTokenService.hasSession()) {
      return of(null);
    }
    return timer(TAB_PRELOAD_DELAY_MS).pipe(switchMap(() => load()));
  }

  private isSaveDataOn(): boolean {
    const connection: { saveData?: boolean } | undefined =
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    return connection?.saveData === true;
  }
}
